import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@velocity/ui';
import moment from 'moment';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { EDevice } from '../../../../redux/sequence/enums';
import { RootState } from '../../../../redux/types';
import WorkFamilyHousingDropdown from '../../../../types/dropdown';
import { PrivateCustomer } from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import {
  FormDateField,
  FormSection,
  FormSelectField,
  FormSelectOption,
} from '../../../common/velocity-form';

interface WorkTemplateDataProps {
  onSubmit: (owner: WorkTemplateData) => void;
  tabButton: boolean;
  privateCustomer: PrivateCustomer | undefined;
  loading?: boolean;
}
export interface WorkTemplateData {
  employment_type: string;
  employment_since: string;
  marital_status: string;
  number_of_children: number;
  residence_type: string;
  residence_since: string;
}

// Dropdown options for Velocity's Select (values must be strings)
const toSelectOptions = (
  options: { label: string; value: string | number }[],
): FormSelectOption[] =>
  options.map(({ label, value }) => ({ label, value: String(value) }));

const employmentTypeOptions = toSelectOptions(
  WorkFamilyHousingDropdown.employment_type,
);
const maritalStatusOptions = toSelectOptions(
  WorkFamilyHousingDropdown.marital_status,
);
const numberOfChildrenOptions = toSelectOptions(
  WorkFamilyHousingDropdown.number_of_children,
);
const residenceTypeOptions = toSelectOptions(
  WorkFamilyHousingDropdown.residence_type,
);

const WorkTemplate: React.FC<WorkTemplateDataProps> = ({
  privateCustomer,
  onSubmit,
  tabButton,
  loading,
}) => {
  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // Get Birth Date from the lease application
  const birthDate = privateCustomer?.person?.date_of_birth;

  // Validation Schema
  const validationSchema = yup.object().shape({
    employment_type: yup.string().required('Pflichtfeld'),
    employment_since: yup
      .string()
      .required('Pflichtfeld')
      .matches(
        /^(((0?[1-9]|[12]\d|3[01])\.(0[13578]|[13578]|1[02])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|[12]\d|30)\.(0[13456789]|[13456789]|1[012])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|1\d|2[0-8])\.0?2\.((1[6-9]|[2-9]\d)\d{2}))|(29\.0?2\.((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/,
        'Bitte Datum eintragen (TT.MM.JJJJ)',
      )
      .test(
        'maxDateToday',
        'Das Datum darf nicht in der Zukunft liegen',
        (value) => {
          return !value || moment(value, 'DD.MM.YYYY') <= moment();
        },
      ),
    marital_status: yup.string().required('Pflichtfeld'),
    number_of_children: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value,
      )
      .required('Pflichtfeld'),
    residence_type: yup.string().required('Pflichtfeld'),
    residence_since: yup
      .string()
      .required('Pflichtfeld')
      .matches(
        /^(((0?[1-9]|[12]\d|3[01])\.(0[13578]|[13578]|1[02])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|[12]\d|30)\.(0[13456789]|[13456789]|1[012])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|1\d|2[0-8])\.0?2\.((1[6-9]|[2-9]\d)\d{2}))|(29\.0?2\.((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/,
        'Bitte Datum eintragen (TT.MM.JJJJ)',
      )
      .test({
        name: 'test-residence',
        message: 'Muss nach Ihrem Geburtsdatum liegen',
        test: (residence_since) =>
          moment(residence_since, 'DD.MM.YYYY').year() >=
          moment(birthDate, 'DD.MM.YYYY').year(),
      })
      .test({
        name: 'maxDateToday',
        message: 'Das Einzugsdatum darf nicht in der Zukunft liegen',
        test: (value) => {
          return !value || moment(value, 'DD.MM.YYYY') <= moment();
        },
      }),
  });

  // useForm hook
  const { control, handleSubmit } = useForm<WorkTemplateData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      employment_type: privateCustomer?.living_details?.employment_type,
      employment_since: privateCustomer?.living_details?.employment_since,

      marital_status: privateCustomer?.living_details?.marital_status,
      number_of_children: privateCustomer?.living_details?.number_of_children,
      residence_type: privateCustomer?.living_details?.residence_type,
      residence_since: privateCustomer?.living_details?.residence_since,
    },
  });

  // OnSubmit
  const submitData = (data: WorkTemplateData) => {
    onSubmit({
      employment_type: data.employment_type,
      employment_since: data.employment_since,
      marital_status: data.marital_status,
      number_of_children: data.number_of_children,
      residence_type: data.residence_type,
      residence_since: data.residence_since,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitData)} noValidate>
      <div className='flex w-full flex-col gap-6'>
        <FormSection title='Beschäftigung'>
          <Controller
            name='employment_type'
            control={control}
            render={({ field, fieldState }) => (
              <FormSelectField
                label='Beschäftigt als?'
                required
                options={employmentTypeOptions}
                {...field}
                errorMessage={fieldState.error?.message}
                dataTestId='select_employment_type'
              />
            )}
          />
          <Controller
            name='employment_since'
            control={control}
            render={({ field, fieldState }) => (
              <FormDateField
                label='Seit wann?'
                required
                autoComplete='off'
                {...field}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </FormSection>

        <FormSection title='Familie & Kinder'>
          <Controller
            name='marital_status'
            control={control}
            render={({ field, fieldState }) => (
              <FormSelectField
                label='Familienstand'
                required
                options={maritalStatusOptions}
                {...field}
                errorMessage={fieldState.error?.message}
                dataTestId='select_marital_status'
              />
            )}
          />
          <Controller
            name='number_of_children'
            control={control}
            render={({ field, fieldState }) => (
              <FormSelectField
                label='Unterhaltspflichtige Kinder?'
                required
                options={numberOfChildrenOptions}
                name={field.name}
                value={
                  field.value === undefined || field.value === null
                    ? ''
                    : String(field.value)
                }
                onChange={(value) =>
                  field.onChange(value === '' ? undefined : Number(value))
                }
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
                dataTestId='select_number_of_children'
              />
            )}
          />
        </FormSection>

        <FormSection title='Wohnsituation'>
          <Controller
            name='residence_type'
            control={control}
            render={({ field, fieldState }) => (
              <FormSelectField
                label={getText('where_living')}
                required
                options={residenceTypeOptions}
                {...field}
                errorMessage={fieldState.error?.message}
                dataTestId='select_residence_type'
              />
            )}
          />
          <Controller
            name='residence_since'
            control={control}
            render={({ field, fieldState }) => (
              <FormDateField
                label='Seit wann?'
                required
                autoComplete='off'
                {...field}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </FormSection>
      </div>

      <>
        {tabButton ? (
          <>
            {device === EDevice.MOBILE ? (
              <ButtonGroup
                className='back-next'
                type='back-next'
                data-testid='disabled-forward-button'
                buttonOneProps={{
                  type: 'button',
                  name: 'form-submit',
                  dataTestId: 'next',
                  children: 'nächster Schritt',
                  loading,
                  onClick: handleSubmit(submitData),
                }}
              />
            ) : (
              <div className='add-additional-person'>
                <Button
                  variant='secondary-outlined'
                  size='m'
                  loading={loading}
                  onClick={handleSubmit(submitData)}
                >
                  nächster Schritt
                </Button>
              </div>
            )}
          </>
        ) : (
          <ButtonGroup
            type='back-next'
            className='back-next'
            buttonOneProps={{
              type: 'submit',
              name: 'form-submit',
              loading,
              dataTestId: 'start-fetching',
              children: 'nächster Schritt',
            }}
          />
        )}
      </>
    </form>
  );
};

export default WorkTemplate;
